import { useEffect, useState, useMemo, Suspense, lazy, forwardRef, useImperativeHandle } from "react";
import AppLoading from "../../components/app-loading/app-loading";
import { ProductCheckoutModel } from "../../models/products/product-checkout-model";
import { useNavigate } from "react-router-dom";
import RouteConstant from "../../constants/route.constants";
import { ProductNewModel } from "../../models/products/product-new-model";
import { useAppDispatch } from "../../redux/hooks";
import { itemCount } from "../../redux/slices/counter";
import ProductStorageHelpers from "../../utils/product-local-storage-wrapper";
import AuthService from "../../services/autentication/auth.service";
import UserService from "../../services/user/user.service";
import RoleService from "../../services/role/role-service";
import { useCustomerDelivery } from "../../providers/CustomerDeliveryProvider";
import { applyDeliveryAvailability } from "../../services/products/customer-delivery.service";

const ProductCardItems = lazy(() => import("./products-card-items"));
const ProductQuickView = lazy(() => import("./product-quick-view"));
const ProductsCheckoutDialog = lazy(() => import("./products-checkout-dialog"));
const ProductNotifyDialog = lazy(() => import("./product-notify-dialog"));

const ProductsCard = forwardRef(({ productsData, catalogProducts = productsData }: any, ref) => {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCustomer, setIsCustomer] = useState(false);


  useEffect(() => {
    const loadUserRole = async () => {
      if (AuthService.isUserLoggedIn()) {
        const user = await UserService.getCurrentUser();

        if (RoleService.isAdmin(user)) {
          setIsAdmin(true);
        }

        if (RoleService.isCustomer(user)) {
          setIsCustomer(true);
        }
      }
    };

    loadUserRole();
  }, []);

  const [isProductQuickView, setIsProductQuickView] = useState(false);
  const [quickViewProduct, setQuickViewProduct] =
    useState<ProductNewModel>(productsData);
  const delivery = useCustomerDelivery();
  const activeQuickViewProduct = useMemo(() => {
    if (!quickViewProduct?.id) return null;
    const original: ProductNewModel = catalogProducts.find((product: ProductNewModel) => product.id === quickViewProduct.id) ?? quickViewProduct;
    return applyDeliveryAvailability([original], delivery.data, delivery.availabilityPending)[0];
  }, [quickViewProduct, catalogProducts, delivery.data, delivery.availabilityPending]);
  const currentPurchasableProduct = (product: ProductNewModel) => {
    const original: ProductNewModel = catalogProducts.find((item: ProductNewModel) => item.id === product.id) ?? product;
    const current = applyDeliveryAvailability([original], delivery.data, delivery.availabilityPending)[0];
    return current.inStock !== 0 && !current.availabilityPending ? current : null;
  };
  const [isProductCheckoutView, setIsProductCheckoutView] = useState(false);
  const [productCheckoutToLocal, setProductCheckoutToLocal] = useState<
    ProductCheckoutModel[]
  >(JSON.parse(localStorage.getItem("checkout-items") || "[]"));
  const [productCheckout, setProductCheckout] =
    useState<ProductCheckoutModel>();

  const [notifyHeader, setNotifyHeader] = useState("");
  const [notifyMessage, setNotifyMessage] = useState("");
  const [isNotifyVisible, setIsNotifyVisible] = useState(false);

  const dispatch = useAppDispatch();

  const showProductQuickView = (product: ProductNewModel) => {
    setQuickViewProduct(product);
    setIsProductQuickView(true);
  };

  // Expose showProductQuickView to parent component
  useImperativeHandle(ref, () => ({
    showProductQuickView
  }));

  const addToCartDialog = (quantity: number) => {
    saveDataInLocalStorage(quantity);
    setIsProductQuickView(false);
  };

  const hideProductQuickView = () => {
    setIsProductQuickView(false);
  };

  const hideProductCheckoutView = () => {
    setIsProductCheckoutView(false);
  };

  const buyNowDialog = (quantity: number) => {
    if (!activeQuickViewProduct || !currentPurchasableProduct(activeQuickViewProduct)) return;
    saveDataInLocalStorage(quantity);
    navigate(RouteConstant.basket);
  };

  const buyNowItemDialog = (product: ProductNewModel, quantity: number) => {
    saveDataInLocalStorageData(quantity, product);
    // navigate(RouteConstant.basket);
  };

  const saveDataInLocalStorage = (quantity: number) => {
    if (!activeQuickViewProduct) return;
    const currentProduct = currentPurchasableProduct(activeQuickViewProduct);
    if (!currentProduct) return;
    // ✅ Always get fresh data from storage to avoid stale state
    const freshCartItems = ProductStorageHelpers.GetProductStorage() || [];

    if (!isDuplicateProduct(currentProduct.id)) {
      const storeData = [
        ...productCheckoutToLocal,
        {
          ...mapToCheckoutProduct(currentProduct, quantity),
        },
      ];
      setProductCheckout(mapToCheckoutProduct(currentProduct, quantity));
      setProductCheckoutToLocal(storeData);
      //localStorage.setItem("checkout-items", JSON.stringify(storeData));
      ProductStorageHelpers.SetProductStorage(storeData);
      dispatch(itemCount(productCheckoutToLocal.length + 1));
    } else {
      setNotifyHeader("Duplicate");
      setNotifyMessage(
        "This product is already in your shopping cart. Please choose another item or continue shopping."
      );

      setIsNotifyVisible(true);
    }
  };

  const saveDataInLocalStorageData = (
    quantity: number,
    product: ProductNewModel
  ) => {
    const currentProduct = currentPurchasableProduct(product);
    if (!currentProduct) return;
    // ✅ Always get fresh data from storage to avoid stale state
    const freshCartItems = ProductStorageHelpers.GetProductStorage() || [];

    if (!isDuplicateProduct(product.id)) {
      const storeData = [
        ...productCheckoutToLocal,
        {
          ...mapToCheckoutProduct(currentProduct, quantity),
        },
      ];
      setProductCheckout(mapToCheckoutProduct(currentProduct, quantity));
      setProductCheckoutToLocal(storeData);
      //ocalStorage.setItem("checkout-items", JSON.stringify(storeData));
      ProductStorageHelpers.SetProductStorage(storeData);
      dispatch(itemCount(productCheckoutToLocal.length + 1));
    }
  };

  const isDuplicateProduct = (id: number): boolean => {
    return productCheckoutToLocal.some((product) => product.id === id);
  };

  const mapToCheckoutProduct = (
    input: ProductNewModel,
    quantity: number
  ): ProductCheckoutModel => {
    return {
      id: input.id,
      name: input.name,
      description: input.description,
      unitPrice: input.unitPrice,
      tax: input.tax,
      weight: input.weight,
      minOrderQty: input.minOrderQty,
      orderMultiples: input.orderMultiples,
      productImageList: input.productImageList,
      categoryID: input.categoryID,
      categoryName: input.categoryName,
      subCategoryID: input.subCategoryID,
      subCategoryName: input.subCategoryName,
      quantity: quantity,
      courierAvailable: input.courierAvailable,
      inStock: input.inStock,
      minQuantity: input.minOrderQty,
      isHomeDeliveryAvailable: input.isHomeDeliveryAvailable
    };
  };

  const loadDataFromLocalStorage = () => {
    // if (localStorage.getItem("checkout-items")) {
    //   const localData = localStorage.getItem("checkout-items");
    //   const parsedData = JSON.parse(localData !== null ? localData : "");
    //   setProductCheckoutToLocal(parsedData);
    // }
    const data = ProductStorageHelpers.GetProductStorage();
    if (data !== undefined) {
      setProductCheckoutToLocal(data);
      dispatch(itemCount(data.length));
    } else {
      setProductCheckoutToLocal([]);
      dispatch(itemCount(0));
    }
  };

  useEffect(() => {
    loadDataFromLocalStorage();
  }, [productsData]);

  useEffect(() => {
    const handleCartUpdate = () => {
      loadDataFromLocalStorage();
    };

    window.addEventListener("cartUpdated", handleCartUpdate);
    return () => {
      window.removeEventListener("cartUpdated", handleCartUpdate);
    };
  }, []);

  const hideNotifyDialog = () => {
    setIsNotifyVisible(false);
  };

  return (
    <>
      <Suspense fallback={<AppLoading />}>
        <div className="grid" style={{ margin: "30", padding: "16px 0" }}>
          {productsData.map((product: ProductNewModel) => {
            return (
              <div className="col-12 sm:col-6 md:col-3 lg:col-3 xl:col-3" key={product.id} style={{ padding: "8px" }}>
                <ProductCardItems
                  productData={product}
                  showProductQuickView={showProductQuickView}
                  buyNowItemDialog={buyNowItemDialog}
                  isAdmin={isAdmin}
                  cartItems={productCheckoutToLocal}
                />
              </div>
            );
          })}
        </div>

        {activeQuickViewProduct ? (
          <ProductQuickView
            productData={activeQuickViewProduct}
            showQuickViewDialog={isProductQuickView}
            addToCartDialog={addToCartDialog}
            hideQuickViewDialog={hideProductQuickView}
            buyNowDialog={buyNowDialog}
            minQuantity={activeQuickViewProduct.minOrderQty}
          />
        ) : null}
        {productCheckout ? (
          <ProductsCheckoutDialog
            productData={productCheckout}
            showCheckoutDialog={isProductCheckoutView}
            hideCheckoutDialog={hideProductCheckoutView}
          />
        ) : null}

        <ProductNotifyDialog
          messageHeader={notifyHeader}
          notifyMessage={notifyMessage}
          isVisible={isNotifyVisible}
          isHide={hideNotifyDialog}
        />
      </Suspense>
    </>
  );
});

export default ProductsCard;
